// Ton, Vibration und Benachrichtigung, wenn ein Timer abgelaufen ist (Lyra SPEZ 6.2, Astrid W8).
//
// Ton: Web Audio, zwei Sinustoene 880 und 1320 Hz je 180 ms, Pause 120 ms, alle 1,5 s, Lautstaerke 0,6, hoechstens
// 60 s. Keine Audiodatei. Der AudioContext entsteht beim ersten "Timer starten" (Nutzergeste), sonst bleibt er auf
// iOS stumm. Vibration: [400, 200, 400, 200, 400] alle 3 s (iOS kann das nicht). Benachrichtigung ueber den Service
// Worker, wenn erlaubt und die App gerade nicht sichtbar ist (Android zeigt new Notification() nicht).

type AudioSessionNavigator = Navigator & { audioSession?: { type: string } };

let ctx: AudioContext | null = null;
let tonTakt: number | null = null;
let vibTakt: number | null = null;
let tonBlockiert = false;

function audio(): AudioContext | null {
  if (ctx) return ctx;
  const Klasse = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Klasse) return null;
  try {
    ctx = new Klasse();
  } catch {
    return null;
  }
  return ctx;
}

/** Bei jeder Nutzergeste im Kochmodus aufrufen ("Timer starten", "Los geht's"): Ton freischalten. */
export function tonVorbereiten(): void {
  const nav = navigator as AudioSessionNavigator;
  // Safari 17: Wiedergabe statt Umgebungston, damit der Stummschalter den Timer nicht verschluckt
  if (nav.audioSession) {
    try {
      nav.audioSession.type = 'playback';
    } catch {
      // nicht unterstuetzt
    }
  }
  const a = audio();
  if (!a) {
    tonBlockiert = true;
    return;
  }
  void a
    .resume()
    .then(() => {
      tonBlockiert = a.state !== 'running';
      // ein unhoerbarer Ton entsperrt iOS endgueltig
      const o = a.createOscillator();
      const g = a.createGain();
      g.gain.value = 0;
      o.connect(g).connect(a.destination);
      o.start();
      o.stop(a.currentTime + 0.01);
    })
    .catch(() => {
      tonBlockiert = true;
    });
}

export function istTonBlockiert(): boolean {
  return tonBlockiert || !audio();
}

export function kannVibrieren(): boolean {
  return typeof navigator.vibrate === 'function';
}

function ton(a: AudioContext, freq: number, start: number, dauer: number, laut: number): void {
  const o = a.createOscillator();
  const g = a.createGain();
  o.type = 'sine';
  o.frequency.value = freq;
  g.gain.setValueAtTime(0, start);
  g.gain.linearRampToValueAtTime(laut, start + 0.01);
  g.gain.setValueAtTime(laut, start + dauer - 0.02);
  g.gain.linearRampToValueAtTime(0, start + dauer);
  o.connect(g).connect(a.destination);
  o.start(start);
  o.stop(start + dauer + 0.01);
}

function klingelEinmal(): void {
  const a = audio();
  if (!a) return;
  if (a.state !== 'running') {
    // Nach dem Hintergrund kann der Kontext haengen: wecken und beim naechsten Takt erneut versuchen
    void a.resume().catch(() => undefined);
    tonBlockiert = true;
    return;
  }
  tonBlockiert = false;
  const t = a.currentTime + 0.02;
  ton(a, 880, t, 0.18, 0.6);
  ton(a, 1320, t + 0.3, 0.18, 0.6);
}

/** Klingeln an (laeuft, bis klingelnAus). Ein Ton fuer alle klingelnden Timer, auch wenn zwei gleichzeitig enden. */
export function klingelnAn(): void {
  if (tonTakt === null) {
    klingelEinmal();
    tonTakt = window.setInterval(klingelEinmal, 1500);
  }
  if (vibTakt === null && kannVibrieren()) {
    const v = () => navigator.vibrate([400, 200, 400, 200, 400]);
    v();
    vibTakt = window.setInterval(v, 3000);
  }
}

export function klingelnAus(): void {
  if (tonTakt !== null) window.clearInterval(tonTakt);
  // Nur abschalten, was lief: vibrate() ohne vorherigen Tipp meldet Chrome als Fehler
  if (vibTakt !== null) {
    window.clearInterval(vibTakt);
    if (kannVibrieren()) navigator.vibrate(0);
  }
  tonTakt = null;
  vibTakt = null;
}

/** Kurzer Gong fuer Erinnerungen ("Jetzt: Bohnen dazugeben"), keine Vibration (Lyra SPEZ 5.7). */
export function gong(): void {
  const a = audio();
  if (!a || a.state !== 'running') return;
  ton(a, 660, a.currentTime + 0.02, 0.35, 0.4);
}

// ---------------------------------------------------------------- Benachrichtigung

export function benachrichtigungMoeglich(): boolean {
  return 'Notification' in window && window.isSecureContext && 'serviceWorker' in navigator;
}

/** Einmal fragen, beim ersten "Timer starten" (Nutzergeste). Ohne HTTPS geht es nicht, dann still weiter. */
export function benachrichtigungErfragen(): void {
  if (!benachrichtigungMoeglich() || Notification.permission !== 'default') return;
  void Notification.requestPermission().catch(() => undefined);
}

export async function benachrichtigen(titel: string, text: string, url: string, tag: string): Promise<void> {
  if (!benachrichtigungMoeglich() || Notification.permission !== 'granted') return;
  try {
    const reg = await navigator.serviceWorker.ready;
    await reg.showNotification(titel, {
      body: text,
      tag,
      icon: '/icons/icon-192.png',
      badge: '/icons/icon-monochrome-512.png',
      requireInteraction: true,
      data: { url },
    } as NotificationOptions);
  } catch {
    // Benachrichtigung ist nur ein Zusatz; Ton und Sheet kommen trotzdem
  }
}
